import { createCwError } from '@/lib/helpers/errors';

import type { CwError } from '@/lib/helpers/errors';
import type { FcwStationSheetProps } from '@/components/organisms/FcwStationSheet.vue';

export interface UseStationsReturn {
  /**
   * Select a station on the map, by ID
   * @param id - ID of the station to select
   */
  selectStation: (id: string | null) => Promise<void>;
  /**
   * The ID of the selected station
   */
  selectedStationId: Ref<string | null>;
  /**
   * The selected station
   */
  selectedStation: Ref<Station | undefined>;
  /**
   * The props to use in station sheet
   */
  selectedStationSheetProps: ComputedRef<
    Omit<FcwStationSheetProps, 'modelValue'>
  >;
  /**
   * Fetch a station data, by ID
   * @param id - ID of the station to fetch
   */
  fetchStation: (id: string) => Promise<void>;
  /**
   * Whether the station data is loading
   */
  isLoading: Ref<boolean>;
  /**
   * Last updated time
   */
  lastUpdatedAt: Ref<Date | null>;
  /**
   * The map configuration for the stations
   */
  stationsMapConfig: ClientState['stationsMapConfig'];
  /**
   * Current error
   */
  error: Ref<CwError | undefined>;
}

export function useStations(stations: Ref<MapStation[]>): UseStationsReturn {
  const { t } = useClientI18n();
  const api = useApi();

  const error = ref<CwError>();

  const isLoading = ref(false);
  const lastUpdatedAt = ref<Date | null>(null);
  const selectedStation = ref<Station>();

  const { stationsMapConfig } = useClientStore().clientState;

  const selectedStationId = ref<string | null>(null);
  const selectedMapStation = computed(() =>
    stations.value.find(station => station.id === selectedStationId.value)
  );
  const selectedStationCoordinates = computed(
    () => selectedMapStation.value?.coordinates as Coordinates
  );

  async function fetchStation(id: string): Promise<void> {
    const { data: stationData, error: stationError } = await api.get(
      `/client/entities/stations/${id}`
    );

    if (stationError.value) {
      error.value = createCwError(stationError.value.errorCode);
    }

    selectedStation.value = stationData.value?.station;
    lastUpdatedAt.value = new Date();
  }

  async function selectStation(id: string | null): Promise<void> {
    selectedStationId.value = id;
    if (!id) return;

    isLoading.value = true;
    await fetchStation(id);
    isLoading.value = false;
  }

  const selectedStationSheetProps = computed<
    Omit<FcwStationSheetProps, 'modelValue'>
  >(() => ({
    name: selectedStation.value?.label ?? '',
    tags: (selectedStation.value?.tags ?? []).map(tag => t(tag)),
    availableBikes: selectedStation.value?.availableBikes ?? 0,
    availableSlots: selectedStation.value?.availableSlots ?? 0,
    address: {
      lines: selectedStation.value?.address ?? [],
      coordinates: {
        lat: selectedStationCoordinates.value?.[1] ?? 0,
        lng: selectedStationCoordinates.value?.[0] ?? 0,
      },
    },
    isClosed: selectedStation.value?.isClosed,
    closedUntil: selectedStation.value?.closedUntil
      ? new Date(selectedStation.value.closedUntil)
      : null,
  }));

  return {
    selectStation,
    selectedStationId,
    selectedStation,
    selectedStationSheetProps,
    fetchStation,
    isLoading,
    lastUpdatedAt,
    stationsMapConfig,
    error,
  };
}
